<template>
  <SubNavigation
    :title="pageData.name"
    :children-links="pageData.parentNodePage?.children"
  />

  <div
    class="bp1680:w-[506px] mx-auto w-[277px] sm:w-[366px] md:w-[354px] lg:w-[452px] xl:w-[426px] 2xl:w-[506px]"
  >
    <Selector
      :placeholder="t('common.all_events')"
      v-model="selectedOption"
      :options="eventTags"
      class="w-full!"
    />
  </div>

  <Container v-if="filteredOnGoingEvents.length > 0">
    <div class="flex flex-col gap-5 md:gap-7.5 xl:gap-10">
      <SectionTitle :title="t('common.ongoing')" class="no-underline!" />
      <CardSlider :slides="filteredOnGoingEvents" />
    </div>
  </Container>

  <Container
    v-if="filteredUpcomingEvents.length > 0"
    class="mt-10 md:mt-15 xl:mt-17.5 2xl:px-0!"
  >
    <CardList :title="t('common.upcoming')" :events="filteredUpcomingEvents" />
  </Container>

  <section
    v-if="!filteredOnGoingEvents.length && !filteredUpcomingEvents.length"
    class="py-20 lg:py-25 xl:py-30"
  >
    <p class="text-center text-2xl md:text-4xl xl:text-6xl">
      {{ t('common.no_events_found') }}
    </p>
  </section>

  <div
    v-if="hasData"
    class="mt-20 mb-30 w-full px-5 md:mx-auto md:w-151.25 md:px-0 lg:mt-25 lg:mb-37.5 lg:w-195 xl:mt-30 xl:mb-42.5 xl:w-223.25! 2xl:w-263.5!"
  >
    <Archives
      :years="years"
      :archive-data="archiveStore"
      :has-more-years="hasMoreYears"
      @load-more="(year) => loadMoreForYear(year)"
      @load-more-years="loadMoreYears"
    />
  </div>
</template>

<script setup lang="ts">
import type { AppEvent, PageData, Tag } from '~/types'

const props = defineProps<{ pageData: PageData }>()

const PER_PAGE = 3
const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()

useDynamicSeo(props.pageData)

const initialCategory = (route.query.category as string) || ''
const initialFilterSlug =
  initialCategory && initialCategory !== 'vse' && initialCategory !== 'all'
    ? initialCategory
    : undefined

const filterSlug = ref<string | undefined>(initialFilterSlug)

const [
  { data: eventTagsData },
  { data: eventsData },
  {
    years,
    archiveStore,
    loadMoreForYear,
    loadMoreYears,
    hasMoreYears,
    hasData,
  },
] = await Promise.all([
  usePersistedFetch('EventTags'),
  usePersistedFetch('GetEvents'),
  useArchive<AppEvent>('Event', PER_PAGE, filterSlug),
])

const eventTags = computed(() => {
  const tags = (eventTagsData.value?.getEventTags as Tag[]) || []
  return [
    {
      name: t('common.all_events'),
      slug: locale.value === 'cs' ? 'vse' : 'all',
    },
    ...tags.map((t) => ({ name: t.name, slug: t.slug[locale.value] })),
  ]
})

const events = computed(() => {
  return (eventsData.value?.getEvents as AppEvent[]) || []
})

const selectedOption = ref(
  eventTags.value.find((opt) => opt.slug === initialCategory) ||
    eventTags.value[0],
)

const appliedOption = ref(selectedOption.value)

let filterTimer: ReturnType<typeof setTimeout> | null = null

watch(selectedOption, (newValue) => {
  if (filterTimer) clearTimeout(filterTimer)

  filterTimer = setTimeout(() => {
    appliedOption.value = newValue

    const slug = newValue?.slug
    filterSlug.value =
      !slug || slug === 'vse' || slug === 'all' ? undefined : slug

    router.push({
      query: {
        ...route.query,
        category: newValue ? newValue.slug : undefined,
      },
    })
  }, 300)
})

const filteredBaseEvents = computed(() => {
  if (!appliedOption.value) return events.value

  const currentSlug = appliedOption.value.slug
  if (currentSlug === 'vse' || currentSlug === 'all') return events.value

  return events.value.filter((event: AppEvent) =>
    event.eventTags?.some((tag: Tag) => tag.slug[locale.value] === currentSlug),
  )
})

const filteredOnGoingEvents = computed(() => {
  return filteredBaseEvents.value.filter((event: AppEvent) => {
    return event.schedule_status.toLowerCase() === 'ongoing'
  })
})

const filteredUpcomingEvents = computed(() => {
  return filteredBaseEvents.value.filter((event: AppEvent) => {
    return event.schedule_status.toLowerCase() === 'upcoming'
  })
})
</script>
